import { useEffect, useRef, useState } from "react";

/**
 * Subscribes to /api/ws/queue/{doctor_id} and yields the latest snapshot.
 * Auto-reconnects on close with backoff.
 */
export default function useQueueSocket(doctorId) {
  const [snap, setSnap] = useState(null);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const aliveRef = useRef(true);

  useEffect(() => {
    if (!doctorId) return;
    aliveRef.current = true;

    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsBase = backend.replace(/^http/, "ws");

    let pingTimer = null;

    const connect = () => {
      if (!aliveRef.current) return;
      try {
        const ws = new WebSocket(`${wsBase}/api/ws/queue/${doctorId}`);
        wsRef.current = ws;
        ws.onmessage = (e) => {
          try { setSnap(JSON.parse(e.data)); retryRef.current = 0; } catch { /* ignore */ }
        };
        ws.onopen = () => {
          retryRef.current = 0;
          pingTimer = setInterval(() => {
            try { ws.send("ping"); } catch { /* ignore */ }
          }, 25000);
        };
        ws.onclose = () => {
          if (pingTimer) clearInterval(pingTimer);
          if (!aliveRef.current) return;
          retryRef.current += 1;
          const delay = Math.min(15000, 1000 * Math.pow(1.7, retryRef.current));
          setTimeout(connect, delay);
        };
        ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
      } catch { /* ignore */ }
    };

    connect();
    return () => {
      aliveRef.current = false;
      if (pingTimer) clearInterval(pingTimer);
      try { wsRef.current?.close(); } catch { /* ignore */ }
    };
  }, [doctorId]);

  return snap;
}
